<script setup lang="ts">
// Migrated to Composition API + shadcn-vue (`Dialog`) per MIGRATION-PROTOCOL.md.
//
// ONLY the unprompted push survives here: `api/instance.js` opens this once per
// page load with every unseen `isPopup` row `/notifications/count` returned.
// One row renders as the notification itself; several render as a digest —
// one list, one dismissal — never as a modal per row. The notifications drawer
// navigates inline instead.
//
// Shown means done: on mount the server records the whole backlog as shown, so
// no later page load opens it again, whatever the person does here. Read state
// is separate — see `markShown`.
//
// This modal is mounted by `popups.js` through its own `createApp`, which
// installs NO plugins — so nothing here may rely on a global property, a global
// component or an app-level provide. `useTranslation`/`useUtils` import tolgee
// and the axios instance directly, and the store is imported rather than taken
// from `useStore()`, so all of them are safe in that bare app.
import { computed, onMounted } from 'vue';

import store from '@/store';
import { useTranslation } from '@/composables/useTranslation';
import { useUtils } from '@/composables/useUtils';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

import { ChevronRight } from 'lucide-vue-next';

/**
 * ONE hub delivery row, as `/notifications/count` ships it under `popups` — the
 * same decorated shape the drawer gets, so the copy reads identically in both.
 */
interface Notification {
  _id: string;
  subjectId?: string;
  courseId?: string;
  /** Joined server-side, so a digest row can name its course. */
  courseName?: string | null;
  /** The frozen copy. `title` is the heading, falling back to `subject`. */
  subject?: string | null;
  title?: string | null;
  /** Frozen HTML, authored by course admins (see the drawer's note). */
  body?: string | null;
  icon?: string | null;
  /** Course colour, joined server-side. */
  color?: string | null;
  /** Resolved server-side; null when the notification leads nowhere. */
  ctaUrl?: string | null;
  ctaLabel?: string | null;
  isSeen?: boolean;
  isReadOnRedirect?: boolean;
  createdAt: string;
}

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    notifications?: Notification[];
  }>(),
  {
    modelValue: false,
    notifications: () => []
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'close'): void;
}>();

const { ct } = useTranslation();
const { api } = useUtils();

// `popups.js` owns the lifetime: it passes `modelValue: true` and unmounts the
// whole app on `close`, so any dismissal just reports back rather than toggling
// local state.
function onOpenChange(value: boolean) {
  if (!value) emit('close');
}

const single = computed(() => (props.notifications.length == 1 ? props.notifications[0] : null));

function heading(notification: Notification) {
  return notification.title ?? notification.subject;
}

function setRead(ids: string[]) {
  if (!ids.length) return;
  for (const _id of ids) {
    // The drawer list is fetched lazily, so the row may not be loaded at all.
    if (!store.state.notifications.some((e: Notification) => e._id === _id)) continue;
    store.commit('updateArrayOfObjects', {
      arrayName: 'notifications',
      fieldName: '_id',
      fieldValue: _id,
      payload: { isSeen: true }
    });
  }
  store.commit('updateState', { notificationsUnread: Math.max(store.state.notificationsUnread - ids.length, 0) });
}

/**
 * Tells the server the digest was shown. `until` is the newest row in it, which
 * also closes out any backlog beyond what the server listed (see
 * `markPopupsShown`) — so a long backlog is ONE interruption, not one per load.
 *
 * Every listed row is read on sight except `isReadOnRedirect`, which stays
 * unread in the bell until the person follows it.
 */
async function markShown() {
  const readIds = props.notifications.filter((e) => !e.isReadOnRedirect && !e.isSeen).map((e) => e._id);
  const until = props.notifications
    .map((e) => e.createdAt)
    .sort()
    .at(-1);
  if (!until) return;
  await api.post('/notifications/popups-shown', {
    until,
    readIds,
    subjectIds: store.state.subjectsList?.map((e: { _id: string }) => e._id).filter(Boolean)
  });
  setRead(readIds);
}

async function follow(notification: Notification) {
  if (notification.isReadOnRedirect && !notification.isSeen) {
    await api.post('/notifications/update-status', {
      notificationIds: [notification._id],
      isSeen: true,
      // Notifications can come from a subject other than the loaded one; the
      // server ignores this when absent or not a subject the user belongs to.
      subjectId: notification.subjectId
    });
    setRead([notification._id]);
  }
  if (notification.ctaUrl) window.location.href = notification.ctaUrl;
  else emit('close');
}

function openDrawer() {
  store.commit('updateState', { isNotificationsDrawerOpen: true });
  emit('close');
}

onMounted(markShown);
</script>

<template>
  <Dialog :open="modelValue" @update:open="onOpenChange">
    <!-- A light scrim, not the default 80% black: this is an unprompted push
         over whatever page the person was on, and should not black it out. -->
    <DialogContent v-if="single" class="sm:max-w-[22.5em]" overlay-class="bg-black/20">
      <DialogHeader class="items-center gap-3">
        <!-- Server-driven Material Symbols ligature, tinted with the course
             colour — an open set, so it stays a glyph rather than a fixed
             Lucide mapping unknown names would fall out of. -->
        <span
          v-if="single.icon"
          class="material-symbols text-[2.5em] leading-none"
          :style="{ color: single.color }"
          aria-hidden="true">
          {{ single.icon }}
        </span>
        <DialogTitle v-if="heading(single)" class="text-center text-lg">
          {{ heading(single) }}
        </DialogTitle>
        <!-- reka-ui's DialogContent warns without a description. When the
             notification carries no body the title still has to be described,
             so it falls back to a screen-reader-only copy of the title.
             `v-html` for the same reason as the drawer: the body is frozen HTML. -->
        <DialogDescription v-if="single.body" as-child class="text-center whitespace-pre-line [&_p]:m-0">
          <div v-html="single.body"></div>
        </DialogDescription>
        <DialogDescription v-else class="sr-only">{{ heading(single) }}</DialogDescription>
      </DialogHeader>

      <DialogFooter v-if="single.ctaUrl">
        <Button class="w-full" @click="follow(single)">
          {{ single.ctaLabel ?? ct('modal_notification.button.navigate') }}
        </Button>
      </DialogFooter>
    </DialogContent>

    <!-- The digest: every row in one list, so a backlog costs one dismissal.
         Rows show the heading and course only — the full body is one click
         away in the bell, and listing bodies would make the modal a page. -->
    <DialogContent v-else class="sm:max-w-[26em]" overlay-class="bg-black/20">
      <DialogHeader>
        <DialogTitle class="text-lg">
          {{ ct('modal_notification.digest.title', { count: notifications.length }) }}
        </DialogTitle>
        <DialogDescription class="sr-only">
          {{ ct('modal_notification.digest.title', { count: notifications.length }) }}
        </DialogDescription>
      </DialogHeader>

      <ul class="m-0 flex max-h-[50vh] list-none flex-col gap-1 overflow-y-auto p-0">
        <li v-for="notification in notifications" :key="notification._id" class="m-0">
          <button
            type="button"
            class="hover:bg-accent/50 flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors"
            @click="follow(notification)">
            <span
              class="material-symbols shrink-0 text-[1.5em] leading-none"
              :style="{ color: notification.color }"
              aria-hidden="true">
              {{ notification.icon ?? 'notifications' }}
            </span>
            <span class="flex min-w-0 flex-1 flex-col">
              <span class="truncate font-medium">{{ heading(notification) }}</span>
              <span v-if="notification.courseName" class="text-muted-foreground truncate text-sm">
                {{ notification.courseName }}
              </span>
            </span>
            <ChevronRight v-if="notification.ctaUrl" :size="16" class="text-muted-foreground shrink-0" />
          </button>
        </li>
      </ul>

      <DialogFooter class="gap-2 sm:justify-between">
        <Button variant="outline" @click="openDrawer()">
          {{ ct('modal_notification.button.open_all') }}
        </Button>
        <Button @click="emit('close')">
          {{ ct('modal_notification.button.dismiss') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
